import { useCallback, useEffect, useRef, useState } from 'react';

import { snackbar } from '@/components/features/Snackbar';
import {
  MicrophoneUnavailableError,
  PcmCapture,
  startPcmCapture,
} from '@/lib/audio/pcmCapture';
import {
  ConnieVoiceConfig,
  ConnieVoiceMessage,
  ConnieVoiceMode,
  getConnieVoiceToken,
  parseConnieVoiceMessage,
  VOICE_CLOSE,
  VoiceAgent,
} from '@/services/connieVoice';

export type ConnieVoiceCallStatus =
  | 'off'
  | 'connecting'
  | 'listening'
  | 'hearing'
  | 'thinking';

/** Pause that ends a turn. FSI clamps it to 500–5000 ms. */
const SILENCE_MS = 1200;

/**
 * The mic level (0–1), kept outside React state: it changes ten times a
 * second, and only the meter needs to re-render, not the whole chat.
 */
export type MicLevelSource = {
  get: () => number;
  subscribe: (listener: () => void) => () => void;
};

const createMicLevel = () => {
  let level = 0;
  const listeners = new Set<() => void>();
  return {
    get: () => level,
    set: (next: number) => {
      if (next === level) return;
      level = next;
      listeners.forEach(listener => listener());
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type ConnieVoiceContext = Omit<
  ConnieVoiceConfig,
  'type' | 'token' | 'mode' | 'silenceMs' | 'agent' | 'documentId'
>;

const AGENT_NAME: Record<VoiceAgent, string> = { connie: 'Connie', tia: 'Tia' };

type UseConnieVoiceCallArgs = {
  /** Who answers. Connie by default; Tia needs `documentId`. */
  agent?: VoiceAgent;
  /** The Library Document a Tia call is about. */
  documentId?: string;
  /** Read when the socket opens, so it carries the current chat session. */
  getContext: () => ConnieVoiceContext;
  /** Turn rendering. Returns true when it showed the message in the chat. */
  onMessage: (message: ConnieVoiceMessage) => boolean | void;
  /** The call ended, for any reason. */
  onEnd?: () => void;
};

const notify = (
  title: string,
  description: string,
  type: 'error' | 'info' = 'error'
) => snackbar({ title, description, type });

type Notice = [string, string, ('error' | 'info')?];

const closeNotice = (code: number, agentName: string): Notice | null => {
  switch (code) {
    case VOICE_CLOSE.CAPACITY:
      return ['Voice is busy', 'Too many calls right now. Try again shortly.'];
    case VOICE_CLOSE.UNAUTHORIZED:
      return ['Could not start the call', 'Voice sign-in failed. Try again.'];
    case VOICE_CLOSE.TRANSCRIPTION_ENDED:
      return [
        'Call ended',
        'Transcription stopped. Start the call again to keep talking.',
      ];
    case VOICE_CLOSE.AGENT_UNAVAILABLE:
      return [
        'Calls are not available yet',
        `You can still type to ${agentName} or send a voice note.`,
        'info',
      ];
    case VOICE_CLOSE.NO_DOCUMENT:
      return [
        'Could not start the call',
        `Open a document to talk to ${agentName}.`,
      ];
    default:
      return null;
  }
};

/**
 * A hands-free voice call with Connie, or Tia about one document, over the
 * FSI voice socket. The server detects the end of each turn; this hook
 * streams the mic, tracks the call state and hands every socket message to
 * `onMessage`.
 */
export const useConnieVoiceCall = ({
  agent = 'connie',
  documentId,
  getContext,
  onMessage,
  onEnd,
}: UseConnieVoiceCallArgs) => {
  const [status, setStatus] = useState<ConnieVoiceCallStatus>('off');
  const [mode, setMode] = useState<ConnieVoiceMode>('auto');
  const [micLevel] = useState(createMicLevel);
  const [caption, setCaption] = useState('');

  const socketRef = useRef<WebSocket | null>(null);
  const captureRef = useRef<PcmCapture | null>(null);
  const readyRef = useRef(false);
  const activeRef = useRef(false);
  const callbacksRef = useRef({ getContext, onMessage, onEnd });
  callbacksRef.current = { getContext, onMessage, onEnd };
  const agentRef = useRef({ agent, documentId });
  agentRef.current = { agent, documentId };
  const agentName = AGENT_NAME[agent];

  const teardown = useCallback(() => {
    const wasActive = activeRef.current;
    activeRef.current = false;
    readyRef.current = false;
    captureRef.current?.stop();
    captureRef.current = null;
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close(VOICE_CLOSE.NORMAL, 'Call ended');
      }
    }
    setStatus('off');
    micLevel.set(0);
    setCaption('');
    if (wasActive) callbacksRef.current.onEnd?.();
  }, [micLevel]);

  const handleMessage = useCallback(
    (message: ConnieVoiceMessage) => {
      switch (message.type) {
        case 'ready':
          readyRef.current = true;
          setMode(message.mode);
          setStatus('listening');
          break;
        case 'transcript':
          // Push-to-talk sends a placeholder while it transcribes.
          if (message.text && message.text !== 'Transcribing...') {
            setCaption(message.text);
            setStatus(current =>
              current === 'thinking' ? current : 'hearing'
            );
          }
          break;
        case 'end_of_turn':
        case 'processing':
          setCaption('');
          setStatus('thinking');
          break;
        case 'listening':
          setCaption('');
          setStatus('listening');
          break;
        case 'idle':
          notify(
            'Call ended',
            `${agentName} hung up after a minute without speech.`,
            'info'
          );
          break;
        default:
          break;
      }

      const shown = callbacksRef.current.onMessage(message);
      if (message.type === 'error' && !shown) {
        notify(`${agentName} voice`, message.message);
      }
    },
    [agentName]
  );

  const connect = useCallback(
    async (retriedAuth: boolean) => {
      let token: string;
      let url: string;
      try {
        ({ token, url } = await getConnieVoiceToken());
      } catch {
        if (!activeRef.current) return;
        teardown();
        notify('Could not start the call', 'Voice is not available right now.');
        return;
      }
      if (!activeRef.current) return;

      const socket = new WebSocket(url);
      socket.binaryType = 'arraybuffer';
      socketRef.current = socket;

      socket.onopen = () => {
        const { agent: callAgent, documentId: callDocumentId } =
          agentRef.current;
        const config: ConnieVoiceConfig = {
          ...callbacksRef.current.getContext(),
          // Connie's config carries no `agent`, exactly as before.
          ...(callAgent !== 'connie' && { agent: callAgent }),
          ...(callDocumentId && { documentId: callDocumentId }),
          type: 'config',
          token,
          mode: 'auto',
          silenceMs: SILENCE_MS,
        };
        socket.send(JSON.stringify(config));
      };

      socket.onmessage = event => {
        if (typeof event.data !== 'string') return;
        const message = parseConnieVoiceMessage(event.data);
        if (message) handleMessage(message);
      };

      socket.onerror = () => {
        // onclose follows with the code.
      };

      socket.onclose = event => {
        if (socketRef.current !== socket) return;
        // A token is single-use and lives a minute: fetch a fresh one once.
        if (
          event.code === VOICE_CLOSE.UNAUTHORIZED &&
          !retriedAuth &&
          !readyRef.current
        ) {
          socketRef.current = null;
          void connect(true);
          return;
        }
        const notice = closeNotice(event.code, agentName);
        const unexpected =
          !notice &&
          event.code !== VOICE_CLOSE.NORMAL &&
          !readyRef.current &&
          activeRef.current;
        teardown();
        if (notice) notify(...notice);
        else if (unexpected)
          notify(
            'Could not start the call',
            `${agentName} voice is unreachable.`
          );
      };
    },
    [agentName, handleMessage, teardown]
  );

  const start = useCallback(async () => {
    if (activeRef.current) return;
    activeRef.current = true;
    setStatus('connecting');
    setCaption('');

    // Ask for the mic first so a refusal shows before any network work.
    try {
      captureRef.current = await startPcmCapture((pcm, rms) => {
        const socket = socketRef.current;
        if (readyRef.current && socket?.readyState === WebSocket.OPEN) {
          socket.send(pcm);
        }
        // Speech RMS sits well under 0.3; scale it to fill the meter.
        // Rounded so near-identical readings don't repaint the meter.
        micLevel.set(Math.round(Math.min(1, rms * 4) * 20) / 20);
      });
    } catch (error) {
      activeRef.current = false;
      setStatus('off');
      notify(
        error instanceof MicrophoneUnavailableError && error.denied
          ? 'Microphone access needed'
          : 'Microphone unavailable',
        error instanceof Error
          ? error.message
          : 'Could not open the microphone.'
      );
      return;
    }
    if (!activeRef.current) {
      captureRef.current?.stop();
      captureRef.current = null;
      return;
    }

    await connect(false);
  }, [connect, micLevel]);

  /** Ends the current turn now instead of waiting for the pause. */
  const endTurn = useCallback(() => {
    const socket = socketRef.current;
    if (readyRef.current && socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'stop' }));
    }
  }, []);

  useEffect(() => teardown, [teardown]);

  return {
    status,
    mode,
    level: micLevel as MicLevelSource,
    caption,
    isActive: status !== 'off',
    start,
    stop: teardown,
    endTurn,
  };
};
